import './KPILibrary.scss';

import { ColDef, GridApi } from '@amzn/gft-ag-grid-enterprise';
import { GFTSearchInput } from '@amzn/gft-frontend-commons';
import { AurigaGrid } from '@components/Common/AurigaGrid/AurigaGrid';
import AurigaLoader from '@components/Common/AurigaLoader';
import { globalGridDefaults } from '@constants/agGrid';
import { KPI_DIMENSION_CONFIG_MAP } from '@constants/page';
import { useAurigaContext } from '@context/AurigaContext';
import { useKPILibraryContext } from '@context/KPILibraryContext';
import AsyncSaveButton from '@deckManagement/components/Common/AsyncSaveButton/AsyncSaveButton';
import ConfirmActionView from '@deckManagement/components/KPILibrary/components/ConfirmActionView';
import EditVariantView from '@deckManagement/components/KPILibrary/components/EditVariantView';
import useGridReady from '@deckManagement/hooks/useGridReady';
import {
  KPILibraryHandle,
  KPILibraryMode,
  KPILibraryProps,
  KPILibrarySelection,
  KPILibraryView,
} from '@deckManagement/types/KPILibrary.types';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { KPIDriversInputSchema } from '@schema/Common.schema';
import { KPIDrivers, KPIVariant } from '@services/api/deckManagement/graphql/schema/API';
import {
  useDeleteKPIVariant,
  useGetVariantUsageSummary,
  useUpdateKPIVariant,
} from '@services/api/deckManagement/hooks/useKPIVariants';
import { generateDriverDisplayOptions } from '@utils/kpiDriverHelpers';
import { isEmpty, isEqual, isNil } from 'lodash';
import { ReactNode, Ref, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { KpiDimension } from 'types/PageSidePanel.types';

import EditRootKPICellRenderer from './CellRenderers/EditRootKPICellRenderer';
import EditVariantCellRenderer from './CellRenderers/EditVariantCellRenderer';

/**
 * KPI Library Management Component to control the lifecycle of KPI Variants used in Auriga Deck Prep.
 *
 * Supports two usage patterns:
 * - Declarative: controlled via isLibraryOpen/setIsLibraryOpen props with onSelect callback
 * - Imperative: controlled via ref with open()/close() methods that return a Promise
 */
const KPILibrary = ({ ref, ...props }: KPILibraryProps & { ref?: Ref<KPILibraryHandle> }) => {
  const {
    mode = KPILibraryMode.DIALOG,
    isLibraryOpen: controlledOpen,
    setIsLibraryOpen,
    rootKPI,
    variant,
    pageDrivers,
    onSelect,
  } = props;
  const { rootKPIs, selectedRootKPI, setSelectedRootKPI, variants, selectedVariant, setSelectedVariant, isLoading } =
    useKPILibraryContext();
  const { toastNotification, showAlert } = useAurigaContext();
  const navigate = useNavigate();
  const gridApiRef = useRef<GridApi | null>(null);
  const { onGridReady } = useGridReady(gridApiRef);

  // Internal open state for imperative usage
  const [internalOpen, setInternalOpen] = useState(false);
  const [isEditingVariant, setIsEditingVariant] = useState(false);
  const [editVariantName, setEditVariantName] = useState('');
  const [editVariantOverrides, setEditVariantOverrides] = useState<KPIDrivers | null>(null);
  const [needsConfirmSave, setNeedsConfirmSave] = useState(false);
  const [needsConfirmDelete, setNeedsConfirmDelete] = useState(false);
  const [summaryExpandedDeckId, setSummaryExpandedDeckId] = useState<string | null>(null);
  // To track whether Variant Summary component has initialized, since we expand the first Deck on Load
  const hasSummaryInitialized = useRef(false);
  // Ref for the promise which returns the KPI Library Selection (RootKPI and Variant)
  const promiseRef = useRef<{ resolve: (v: KPILibrarySelection | null) => void } | null>(null);

  // Fetch variant usage summary when editing a variant
  const { data: variantUsageSummary, isLoading: isUsageSummaryLoading } = useGetVariantUsageSummary({
    kpi_variant_id: selectedVariant?.kpi_variant_id ?? '',
    pageUsageFields: ['page_id', 'page_name', 'deckline_count'], // Omits kpi_variant_ids as unnecessary in this view
    options: { enabled: !!selectedVariant && isEditingVariant },
  });

  // Mutation to save variant changes
  const { mutate: updateVariant, isPending: isSavingVariant } = useUpdateKPIVariant();
  // Mutation to delete variant
  const { mutate: deleteVariant, isPending: isDeletingVariant } = useDeleteKPIVariant();

  // Derived open state: controlled prop takes priority, falls back to internal
  const isOpen = controlledOpen ?? internalOpen;

  // Search state for Root KPI filtering (in Root KPI View)
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResultCount, setSearchResultCount] = useState<number | null>(null);

  // Whether to open the library in PAGE or DIALOG modal mode
  const isDialogMode = mode === KPILibraryMode.DIALOG;
  // Enables the Discard button functionality (Discard button is disabled until changes exist)
  const hasVariantChanges = useMemo(
    () =>
      editVariantName !== (selectedVariant?.variant_name ?? '') ||
      JSON.stringify(editVariantOverrides) !== JSON.stringify(selectedVariant?.variant_overrides ?? null),
    [editVariantName, editVariantOverrides, selectedVariant?.variant_name, selectedVariant?.variant_overrides],
  );

  // Driver display options for Edit Variant view
  // page_drivers are intentionally ignored here — in the Edit Variant context, a dimension is active
  // if the variant includes a non-null value for it, and users can activate any dimension freely.
  const editVariantDriverDisplayOptions = useMemo(() => {
    const options = generateDriverDisplayOptions(editVariantOverrides) ?? {};
    return Object.fromEntries(
      Object.entries(options).map(([key, entry]) => {
        const dimensionKey = entry?.kpiDimension as keyof KPIDrivers | undefined;
        const active = !!dimensionKey && !isNil(editVariantOverrides?.[dimensionKey]);
        // Override the active state: a dimension is active only when it has a non-null value in overrides.
        // Absent or null keys mean the dimension cascades from page_drivers (inactive).
        return [key, entry ? { ...entry, active } : entry];
      }),
    );
  }, [editVariantOverrides]);

  /**
   * Returns the current view state of the KPI Library Modal
   * Derived view: Edit Variant takes priority, then Variant View if root KPI selected, else the Root KPI View list
   */
  const getView = (): KPILibraryView => {
    if (isEditingVariant) return KPILibraryView.EDIT_VARIANT_VIEW;
    if (selectedRootKPI) return KPILibraryView.VARIANT_VIEW;
    return KPILibraryView.ROOT_KPI_VIEW;
  };

  // For tracking shifts in view required for rendering different content
  const isRootKPIView = getView() === KPILibraryView.ROOT_KPI_VIEW;
  const isEditVariantView = getView() === KPILibraryView.EDIT_VARIANT_VIEW;

  /**
   * Returns the appropriate title text for the current view
   */
  const getTitleText = (): string => {
    if (needsConfirmDelete)
      return `Delete Variant KPI - ${selectedRootKPI?.root_kpi_name} (${selectedVariant?.variant_name})`;
    if (needsConfirmSave) return 'Updating Variant KPI';
    if (isEditVariantView) return 'Edit Variant KPI';
    if (isRootKPIView) return 'KPI Library';
    return `KPI Variant Library - ${selectedRootKPI?.root_kpi_name}`;
  };

  /**
   * Returns the appropriate RETURN navigation button for the current view state
   */
  const getReturnBrowseButton = (): ReactNode => {
    const editVariantReturnText = '← Return to Edit Variant KPI';

    if (needsConfirmDelete) {
      return (
        <Button variant='text' onClick={() => setNeedsConfirmDelete(false)}>
          {editVariantReturnText}
        </Button>
      );
    }
    if (needsConfirmSave) {
      return (
        <Button variant='text' onClick={() => setNeedsConfirmSave(false)}>
          {editVariantReturnText}
        </Button>
      );
    }
    if (isEditVariantView) {
      return (
        <Button
          variant='text'
          onClick={() => {
            setIsEditingVariant(false);
            hasSummaryInitialized.current = false;
            setSummaryExpandedDeckId(null);
          }}
        >
          ← Back to Variants
        </Button>
      );
    }
    if (!isRootKPIView) {
      return (
        <Button variant='text' onClick={() => setSelectedRootKPI(null)}>
          ← Browse Library
        </Button>
      );
    }
    return null;
  };

  /**
   * Returns the Discard Changes button that resets edit state to original variant values
   */
  const getDiscardButton = useCallback(
    (): ReactNode => (
      <Button
        variant='outlined'
        disabled={!hasVariantChanges}
        onClick={() => {
          setEditVariantName(selectedVariant?.variant_name ?? '');
          setEditVariantOverrides(selectedVariant?.variant_overrides ?? null);
          setNeedsConfirmSave(false);
          setNeedsConfirmDelete(false);
        }}
      >
        Discard Changes
      </Button>
    ),
    [hasVariantChanges, selectedVariant?.variant_name, selectedVariant?.variant_overrides],
  );

  /**
   * Saves the edited variant name and overrides to the backend.
   */
  const handleSaveVariant = useCallback(() => {
    if (!selectedVariant || !selectedRootKPI) return;

    // Strip null/undefined values from overrides — only non-null entries represent active overrides.
    // Null values are legacy artifacts; dimensions without overrides should be omitted entirely
    // so they cascade from page_drivers.
    const sanitizedOverrides: Partial<typeof editVariantOverrides> = editVariantOverrides
      ? Object.fromEntries(Object.entries(editVariantOverrides).filter(([, v]) => !isNil(v)))
      : null;

    const update_params: Parameters<typeof updateVariant>[0] = {
      root_kpi_id: selectedRootKPI.root_kpi_id,
      kpi_variant_id: selectedVariant.kpi_variant_id,
      variant_name: editVariantName,
      variant_overrides: KPIDriversInputSchema.parse(sanitizedOverrides),
    };

    updateVariant(update_params, {
      onSuccess: () => {
        toastNotification({ message: 'Variant saved successfully.', visible: true, type: 'success' });
        setIsEditingVariant(false);
        setNeedsConfirmSave(false);
      },
    });
  }, [selectedVariant, selectedRootKPI, editVariantName, editVariantOverrides, updateVariant, toastNotification]);

  /**
   * Deletes the variant and navigates back. If the root KPI was auto-deleted (last variant deleted), returns to the Root KPI view.
   */
  const handleDeleteVariant = useCallback(() => {
    if (!selectedVariant || !selectedRootKPI) return;
    deleteVariant(
      {
        root_kpi_id: selectedRootKPI.root_kpi_id,
        kpi_variant_id: selectedVariant.kpi_variant_id,
      },
      {
        onSuccess: (data) => {
          toastNotification({ message: 'Variant deleted successfully.', visible: true, type: 'success' });
          setIsEditingVariant(false);
          setNeedsConfirmDelete(false);
          if (data.root_kpi_auto_deleted) {
            setSelectedRootKPI(null);
          }
          setSelectedVariant(null);
        },
      },
    );
  }, [selectedVariant, selectedRootKPI, deleteVariant, toastNotification, setSelectedRootKPI, setSelectedVariant]);

  /**
   * Returns the actions to display in the Edit Variant View
   */
  const getEditVariantActions = useCallback(
    () => (
      <>
        <Button variant='outlined' color='error' startIcon={<DeleteIcon />} onClick={() => setNeedsConfirmDelete(true)}>
          Delete KPI
        </Button>
        {getDiscardButton()}
        <AsyncSaveButton
          inProgress={isSavingVariant}
          inProgressContent='Saving...'
          buttonProps={{
            variant: 'contained',
            disabled: !hasVariantChanges || isSavingVariant,
            onClick: () => {
              if ((variantUsageSummary?.total_page_count ?? 0) > 1) {
                setNeedsConfirmSave(true);
              } else {
                handleSaveVariant();
              }
            },
          }}
        >
          Save Changes to Variant
        </AsyncSaveButton>
      </>
    ),
    [getDiscardButton, isSavingVariant, hasVariantChanges, variantUsageSummary?.total_page_count, handleSaveVariant],
  );

  /**
   * Returns the actions for the confirm save view (Discard + Save Across All Pages)
   */
  const getConfirmSaveActions = useCallback(
    () => (
      <>
        {getDiscardButton()}
        <AsyncSaveButton
          inProgress={isSavingVariant}
          inProgressContent='Saving...'
          buttonProps={{ variant: 'contained', disabled: isSavingVariant, onClick: handleSaveVariant }}
        >
          Save Across All Pages
        </AsyncSaveButton>
      </>
    ),
    [getDiscardButton, isSavingVariant, handleSaveVariant],
  );

  /**
   * Returns the actions for the confirm delete view
   */
  const getConfirmDeleteActions = useCallback(
    () => (
      <>
        {getDiscardButton()}
        <AsyncSaveButton
          inProgress={isDeletingVariant}
          inProgressContent='Deleting...'
          buttonProps={{
            variant: 'contained',
            color: 'error',
            disabled: isDeletingVariant,
            onClick: handleDeleteVariant,
          }}
        >
          Delete All KPI Instances
        </AsyncSaveButton>
      </>
    ),
    [getDiscardButton, isDeletingVariant, handleDeleteVariant],
  );

  /**
   * Closes the modal and resolves any pending imperative promise with null.
   */
  const handleClose = useCallback(() => {
    setIsLibraryOpen?.(false);
    setInternalOpen(false);
    setIsEditingVariant(false);
    setNeedsConfirmSave(false);
    setNeedsConfirmDelete(false);
    hasSummaryInitialized.current = false;
    setSummaryExpandedDeckId(null);
    promiseRef.current?.resolve(null);
    promiseRef.current = null;
  }, [setIsLibraryOpen]);

  /**
   * Handles the "Assign KPI" action — resolves both declarative and imperative paths.
   */
  const handleAssign = useCallback(() => {
    if (!selectedVariant || !selectedRootKPI) return;
    const selection: KPILibrarySelection = { rootKPI: selectedRootKPI, variant: selectedVariant };
    onSelect?.(selection);
    promiseRef.current?.resolve(selection);
    promiseRef.current = null;
    setIsLibraryOpen?.(false);
    setInternalOpen(false);
  }, [selectedVariant, selectedRootKPI, onSelect, setIsLibraryOpen]);

  // Expose imperative API via ref
  useImperativeHandle(
    ref,
    () => ({
      open: (view?: KPILibraryView) => {
        // Resolve any previously pending promise with null before overwriting
        promiseRef.current?.resolve(null);
        promiseRef.current = null;

        setInternalOpen(true);
        setIsEditingVariant(false);

        if (view === KPILibraryView.EDIT_VARIANT_VIEW && rootKPI && variant) {
          setSelectedRootKPI(rootKPI);
          setSelectedVariant(variant);
          setEditVariantName(variant.variant_name);
          setEditVariantOverrides(variant.variant_overrides ?? null);
          setIsEditingVariant(true);
        } else if (view === KPILibraryView.VARIANT_VIEW && rootKPI) {
          setSelectedRootKPI(rootKPI);
          setSelectedVariant(null);
        } else {
          setSelectedRootKPI(null);
          setSelectedVariant(null);
        }

        return new Promise<KPILibrarySelection | null>((resolve) => {
          promiseRef.current = { resolve };
        });
      },
      close: handleClose,
    }),
    [rootKPI, variant, setSelectedRootKPI, setSelectedVariant, handleClose],
  );

  // Syncs the local edit state when selectedVariant updates externally (e.g. subscription update) and User has no pending edits
  const previousVariantRef = useRef(selectedVariant);
  useEffect(() => {
    const previousVariant = previousVariantRef.current;
    if (
      selectedVariant &&
      previousVariant &&
      isEditingVariant &&
      previousVariant.kpi_variant_id === selectedVariant.kpi_variant_id &&
      !isEqual(previousVariant, selectedVariant)
    ) {
      const userHasEditedName = editVariantName !== (previousVariant.variant_name ?? '');
      const userHasEditedOverrides = !isEqual(editVariantOverrides, previousVariant.variant_overrides ?? null);

      if (!userHasEditedName && !userHasEditedOverrides) {
        // Only notify if the variant's editable fields actually changed
        const nameChanged = previousVariant.variant_name !== selectedVariant.variant_name;
        const overridesChanged = !isEqual(previousVariant.variant_overrides, selectedVariant.variant_overrides);
        setEditVariantName(selectedVariant.variant_name);
        setEditVariantOverrides(selectedVariant.variant_overrides ?? null);
        if (nameChanged || overridesChanged) {
          toastNotification({
            message: 'This variant was updated externally.',
            visible: true,
            type: 'info',
          });
        }
      } else {
        // Show dismissable Alert if updated after Edits occurred
        showAlert({
          message: 'This variant was updated externally. Your unsaved changes may conflict.',
          type: 'warning',
          visible: true,
          dismissible: true,
        });
      }
    }

    // Update the ref so it stays current
    previousVariantRef.current = selectedVariant;
  }, [editVariantName, editVariantOverrides, isEditingVariant, selectedVariant, showAlert, toastNotification]);

  // Cleanup: resolve pending promise on unmount to prevent hanging callers
  useEffect(() => {
    return () => {
      promiseRef.current?.resolve(null);
      promiseRef.current = null;
    };
  }, []);

  // Sync props into context when provided (such as when opening directly into Variant or Edit Variant view)
  // Guard: skip on imperative path (promiseRef.current is set synchronously in open() before re-render)
  useEffect(() => {
    if (isOpen && rootKPI && !promiseRef.current) {
      setSelectedRootKPI(rootKPI);
      if (variant) {
        setSelectedVariant(variant);
        setEditVariantName(variant.variant_name);
        setEditVariantOverrides(variant.variant_overrides ?? null);
        setIsEditingVariant(true);
      } else {
        setSelectedVariant(null);
      }
    }
  }, [isOpen, rootKPI, variant, setSelectedRootKPI, setSelectedVariant]);

  // Reset context state when dialog opens without a rootKPI prop (ensures fresh root KPI list view)
  // Guard: skip on imperative path to avoid overriding open() intent
  useEffect(() => {
    if (isOpen && !rootKPI && !promiseRef.current) {
      setSelectedRootKPI(null);
      setSelectedVariant(null);
      setIsEditingVariant(false);
    }
  }, [isOpen, rootKPI, setSelectedRootKPI, setSelectedVariant]);

  // Clears the search state when leaving Root KPI view to prevent stale filter on re-mount
  useEffect(() => {
    if (!isRootKPIView) {
      setSearchTerm('');
      setSearchResultCount(null);
    }
  }, [isRootKPIView]);

  // Expand the first deck in usage summary on initial data load
  useEffect(() => {
    if (!hasSummaryInitialized.current && variantUsageSummary?.deck_usage?.[0]?.deck_id) {
      setSummaryExpandedDeckId(variantUsageSummary.deck_usage[0].deck_id);
      hasSummaryInitialized.current = true;
    }
  }, [variantUsageSummary]);

  /**
   * Column definitions for the Root KPI list view
   */
  const rootKPIColDefs: ColDef[] = useMemo(
    () => [
      {
        field: 'root_kpi_name',
        headerName: 'Name',
        flex: 4,
        sort: 'asc',
      },
      {
        headerName: 'Variants',
        maxWidth: 120,
        valueGetter: (params) => params.data?.variants?.length ?? 0,
      },
      {
        headerName: 'Edit',
        maxWidth: 100,
        cellRenderer: EditRootKPICellRenderer,
        sortable: false,
      },
    ],
    [],
  );

  /**
   * Column definitions for the Variant list view
   */
  const variantColDefs: ColDef[] = useMemo(
    () => [
      {
        headerName: 'Name',
        flex: 2,
        valueGetter: () => selectedRootKPI?.root_kpi_name ?? '', // Relies on Root KPI existing in context
        sortable: false, // All Root KPI Names would be the same in this view; can safely turn sortable off here
      },
      {
        field: 'variant_name',
        headerName: 'Variant',
        flex: 2,
        sort: 'asc',
      },
      {
        field: 'kpi_type',
        headerName: 'Type',
        maxWidth: 120,
      },
      {
        headerName: 'Edit',
        maxWidth: 100,
        cellRenderer: EditVariantCellRenderer,
        cellRendererParams: {
          onEdit: (variantToEdit: KPIVariant) => {
            setSelectedVariant(variantToEdit);
            setEditVariantName(variantToEdit.variant_name);
            setEditVariantOverrides(variantToEdit.variant_overrides ?? null);
            setIsEditingVariant(true);
          },
        },
        sortable: false,
      },
    ],
    [selectedRootKPI?.root_kpi_name, setSelectedVariant],
  );

  /**
   * Default column definitions for all grids in the component
   */
  const defaultColDef = useMemo(
    () => ({
      resizable: true,
      sortable: true,
    }),
    [],
  );

  // Title related modifications due to displayed view changes
  const titleText = getTitleText();
  const titleActions = (
    <Box className='kpi-library-title-actions'>
      {getReturnBrowseButton()}
      {isEditVariantView && !needsConfirmSave && !needsConfirmDelete && !isDialogMode && getEditVariantActions()}
      {needsConfirmSave && !isDialogMode && getConfirmSaveActions()}
      {needsConfirmDelete && !isDialogMode && getConfirmDeleteActions()}
      {isDialogMode && (
        <IconButton onClick={handleClose} className='kpi-library-close' aria-label='close'>
          <CloseIcon />
        </IconButton>
      )}
    </Box>
  );

  /**
   * Shared content rendered in both dialog and page modes.
   */
  const libraryContent = (
    <>
      {isDialogMode ? (
        <>
          <DialogTitle className='kpi-library-title'>
            {titleText}
            {titleActions}
          </DialogTitle>
          <Divider />
        </>
      ) : (
        <Box className='kpi-library-header'>
          <Box className='kpi-library-title'>
            <Button variant='text' onClick={() => navigate('/deck-preparation')}>
              ← Home
            </Button>
            <Typography variant='h5'>{titleText}</Typography>
            {titleActions}
          </Box>
          <Divider />
        </Box>
      )}
      {isLoading && <AurigaLoader loaderText='Loading KPI Library...' />}
      {!isLoading && isRootKPIView && !isEmpty(rootKPIs) && (
        <Box className='kpi-library-container'>
          <Box className='kpi-library-controls'>
            <GFTSearchInput
              className='kpi-library-search-input'
              placeholder='Search Root KPIs...'
              minSearchLength={2}
              value={searchTerm}
              onChange={(value: string) => setSearchTerm(value)}
              onSearch={(value: string) => {
                // AG Grid Quick Filter: https://www.ag-grid.com/javascript-data-grid/filter-quick/
                gridApiRef.current?.setGridOption('quickFilterText', value);
                setSearchResultCount(gridApiRef.current?.getDisplayedRowCount() ?? null);
              }}
              onClear={() => {
                setSearchTerm('');
                gridApiRef.current?.setGridOption('quickFilterText', '');
                setSearchResultCount(null);
              }}
            />
            {searchResultCount !== null && (
              <Typography variant='body2' className='kpi-search-results-text'>
                {`${searchResultCount} results found.`}
              </Typography>
            )}
          </Box>
          <Box className='grid-container root-kpis-grid'>
            <AurigaGrid
              {...globalGridDefaults}
              rowData={rootKPIs}
              columnDefs={rootKPIColDefs}
              defaultColDef={defaultColDef}
              rowClass='selectable-row'
              getRowId={(params) => params.data.root_kpi_id}
              onGridReady={onGridReady}
              animateRows
            />
          </Box>
        </Box>
      )}
      {!isLoading && !isRootKPIView && !isEditVariantView && selectedRootKPI && (
        <>
          <Box className='grid-container variant-grid'>
            <AurigaGrid
              {...globalGridDefaults}
              rowData={variants}
              columnDefs={variantColDefs}
              defaultColDef={defaultColDef}
              rowClass='selectable-row'
              getRowId={(params) => params.data.kpi_variant_id}
              onGridReady={onGridReady}
              // Selection only applies in dialog mode (for Assign KPI workflow)
              // enableClickSelection allows selection via Click on row, and deselection via CMD(or Windows Key)+Click on row
              {...(isDialogMode && {
                rowSelection: { mode: 'singleRow' as const, checkboxes: true, enableClickSelection: true },
                onSelectionChanged: (event: { api: GridApi }) => {
                  const selected = event.api.getSelectedRows();
                  setSelectedVariant(selected.length > 0 ? selected[0] : null);
                },
              })}
              animateRows
            />
          </Box>
          {isDialogMode && (
            <DialogActions>
              <Button variant='outlined' onClick={handleClose}>
                Cancel
              </Button>
              <Button variant='contained' disabled={!selectedVariant} onClick={handleAssign}>
                Assign KPI
              </Button>
            </DialogActions>
          )}
        </>
      )}
      {!isLoading &&
        isEditVariantView &&
        !needsConfirmSave &&
        !needsConfirmDelete &&
        selectedRootKPI &&
        selectedVariant && (
          <EditVariantView
            selectedRootKPI={selectedRootKPI}
            selectedVariant={selectedVariant}
            editVariantName={editVariantName}
            onVariantNameChange={setEditVariantName}
            driverDisplayOptions={editVariantDriverDisplayOptions}
            placeholderDrivers={pageDrivers}
            onDriverChange={(_key: KpiDimension, updates: Partial<KPIDrivers>) => {
              setEditVariantOverrides((prev) => ({ ...prev, ...updates }) as KPIDrivers);
            }}
            onDriverToggle={(key: KpiDimension, checked: boolean) => {
              const { hierarchyAccessor } = KPI_DIMENSION_CONFIG_MAP[key]?.decklineDrivers ?? {};
              if (!checked) {
                // Toggling off: remove the dimension from overrides so it cascades from the page driver
                setEditVariantOverrides((prev) => {
                  if (!prev) return prev;
                  const keysToRemove = new Set<string>([key]);
                  if (hierarchyAccessor) keysToRemove.add(hierarchyAccessor);
                  return Object.fromEntries(Object.entries(prev).filter(([k]) => !keysToRemove.has(k))) as KPIDrivers;
                });
              } else {
                // Toggling on: restore original value/hierarchy or set empty string to enable editing
                const originalValue = selectedVariant.variant_overrides?.[key as keyof KPIDrivers] ?? '';
                const originalHierarchy = hierarchyAccessor
                  ? (selectedVariant.variant_overrides?.[hierarchyAccessor as keyof KPIDrivers] ?? '')
                  : undefined;
                setEditVariantOverrides(
                  (prev) =>
                    ({
                      ...prev,
                      [key]: originalValue,
                      ...(hierarchyAccessor && { [hierarchyAccessor]: originalHierarchy }),
                    }) as KPIDrivers,
                );
              }
            }}
            usageSummary={variantUsageSummary}
            isUsageSummaryLoading={isUsageSummaryLoading}
            expandedDeckId={summaryExpandedDeckId}
            onExpandChange={setSummaryExpandedDeckId}
            isDialogMode={isDialogMode}
            editVariantActions={getEditVariantActions()}
          />
        )}
      {!isLoading && needsConfirmSave && selectedRootKPI && selectedVariant && (
        <ConfirmActionView
          wrapperClassName='kpi-library-confirm-save-content'
          msgClassName='confirm-save-message'
          messages={[
            'This variant KPI is currently used in multiple pages. Saving will update this KPI across all locations.',
            'Are you sure you want to continue?',
          ]}
          usageSummary={variantUsageSummary}
          isUsageSummaryLoading={isUsageSummaryLoading}
          expandedDeckId={summaryExpandedDeckId}
          onExpandChange={setSummaryExpandedDeckId}
          isDialogMode={isDialogMode}
          confirmActions={getConfirmSaveActions()}
        />
      )}
      {!isLoading && needsConfirmDelete && selectedRootKPI && selectedVariant && (
        <ConfirmActionView
          wrapperClassName='kpi-library-confirm-delete-content'
          msgClassName='confirm-delete-message'
          messages={[
            'This variant KPI is currently used in multiple pages. Deleting will update this KPI and its associated decklines across all locations.',
            'This cannot be undone.',
          ]}
          usageSummary={variantUsageSummary}
          isUsageSummaryLoading={isUsageSummaryLoading}
          expandedDeckId={summaryExpandedDeckId}
          onExpandChange={setSummaryExpandedDeckId}
          isDialogMode={isDialogMode}
          confirmActions={getConfirmDeleteActions()}
        />
      )}
    </>
  );

  if (isDialogMode) {
    return (
      <Dialog className='kpi-library-dialog' open={isOpen} onClose={handleClose}>
        {libraryContent}
      </Dialog>
    );
  }

  return <Box className='kpi-library-page'>{libraryContent}</Box>;
};

export default KPILibrary;
